import { PreconditionError, ValidationError } from "bp-common-models-lib";
import { Observable, of as observableOf } from "rxjs";

import { catchError, map, share, switchMap } from "rxjs/operators";
import { Injectable } from "@angular/core";

import { parseList } from "@helpers/functions";
import { SocketService } from "../socket.service";
import { AssetWalletService } from "./asset_wallet.service";
import { CryptoTransactionNotifyService } from "../notify/crypto_transaction.notify.service";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { CRYPTO_TRANSACTIONS_ROOM, CRYPTO_WALLETS_ROOM } from "@helpers/constants";
import { CryptoTransaction } from "@models/transaction";
import { GetPendingTradesGQLService } from "@gql/offer";
import { Address, Cryptocoin, CryptoWallet, ErrorClass } from "@models";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { CryptocoinService } from "@main-services/currency";
import { DestructorService } from "@core-services/destructor.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { WalletsBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallets-balance-status.model";

export interface WalletViewHelper {
	wallet: CryptoWallet;
	currentAddress: Address;
	otherAddresses: Address[];
}

const name = ServiceNames.CRYPTO_WALLET;

@Injectable({
	providedIn: "root"
})
export class CryptoWalletService extends AssetWalletService<CryptoWallet, CryptoTransaction, Cryptocoin> {
	public name: string = ServiceNames.CRYPTO_WALLET;

	private canCreateMore: boolean = false;
	private cannotCreateReason: string = "";

	public TxType = CryptoTransaction;
	public WalletType = CryptoWallet;

	protected transactionSocketRoomName: string = CRYPTO_TRANSACTIONS_ROOM;
	protected walletSocketRoomName: string = CRYPTO_WALLETS_ROOM;

	public constructor(
		protected currencyService: CryptocoinService,
		protected apicallService: ApicallService,
		protected infoService: InfoService,
		protected socketService: SocketService,
		protected translationService: TranslationService,
		protected initService: InitService,
		protected transactionNotifyService: CryptoTransactionNotifyService,
		protected destructor: DestructorService,
		protected getPendingTradesGQLService: GetPendingTradesGQLService,
		protected walletBalanceFacade: WalletsBalancesFacadeService
	) {
		super(initService, destructor, name, getPendingTradesGQLService);
	}

	public getCryptoBalancesStatus$ = (): Observable<WalletsBalanceStatus> =>
		this.walletBalanceFacade.getCryptoBalancesStatus$();

	public getCanCreateMore(): boolean {
		return this.canCreateMore;
	}

	public getCannotCreateReason(): string {
		return this.cannotCreateReason;
	}

	public getParseData(wallets): CryptoWallet[] {
		return parseList(wallets["attributes"]["cryptocoin"]["attributes"]["wallets"], CryptoWallet);
	}

	public async cancel(t: CryptoTransaction): Promise<CryptoTransaction> {
		const oldTransaction = new CryptoTransaction();
		Object.assign(oldTransaction, t);
		const call = this.apicallService.deleteRequest(
			"wallet/" + t.getWalletId() + "/transaction/" + t.getId(),
			new ApicallOption(CryptoTransaction)
		);
		return super.cancelSub(call, t, oldTransaction).toPromise();
	}

	public createNewAddress(wallet: CryptoWallet): Observable<WalletViewHelper> {
		return this.apicallService.post(`wallet/${wallet.getId()}/addresses`, new ApicallOption(Address)).pipe(
			map((res) => {
				const addresses = res;
				return {
					wallet: wallet,
					currentAddress: addresses[addresses.length - 1],
					otherAddresses: addresses.slice(0, addresses.length - 1)
				};
			}),
			share()
		);
	}

	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	private getApiAddressCall(wallet): Observable<any> {
		const option = new ApicallOption();
		option.raw = true;
		return wallet.isNew()
			? this.apicallService.post(`assets/${wallet.getCurrencyId()}/wallet-addresses`, option)
			: this.apicallService.get(`wallet/${wallet.getId()}/addresses`, option);
	}

	public getAddressesForWallet(wallet): Observable<CryptoWallet> {
		const coin: Cryptocoin = this.currencyService.getItemById(wallet.getCurrencyId());
		let addressWallet: CryptoWallet = wallet;
		// For ERC20 we always check addresses of the FIRST wallet of this coin returned by backend
		if (coin.isErc20Family()) {
			addressWallet = this.list.filter((w) => w.getCurrencyId() === coin.getId())[0];
		}
		return this.getApiAddressCall(addressWallet).pipe(
			switchMap((addresses: Address) => {
				this.canCreateMore = addresses["can_create_more"];
				this.cannotCreateReason = addresses["cannot_create_reason"] || "";
				const addressData: Address[] = parseList(addresses["data"], Address);

				if (wallet.isNew() && !!addressData[0] && !!addressData[0].getWalletId()) {
					// Get the actual wallet
					return this.apicallService
						.get("wallet/" + addressData[0].getWalletId(), new ApicallOption(this.WalletType))
						.pipe(
							map((res) => {
								const realWallet = res[0];
								this.upsert(realWallet);
								if (wallet.getCurrencyId() === realWallet.getCurrencyId()) {
									this.deleteItem(wallet);
								} // Remove dummy wallet
								realWallet.addresses = addressData;
								return realWallet;
							})
						);
				}
				wallet.addresses = addressData;
				return observableOf(wallet);
			}),
			catchError((error) => {
				if (error instanceof ErrorClass) {
					if (error instanceof PreconditionError) {
						throw error;
					}

					if (
						error instanceof ValidationError &&
						error.containsCode("multichain.coin_network_id.no_coin_network_for_given_asset")
					) {
						return observableOf(null);
					}

					this.infoService.setError(error);
					return observableOf(null);
				}
				console.error(error);
				return [];
			})
		);
	}
	public resetReason(): void {
		this.cannotCreateReason = "";
	}
}
